/**
 * NutritionDialog — bottom-sheet on mobile, centered card on desktop.
 *
 * Wraps Headless UI's `<Dialog>` so consumers don't reimplement focus
 * trap, Esc handling, or scrim. Mobile slides up and fills 90 % of
 * viewport height (with safe-area-inset for iOS); desktop renders as a
 * centered max-w-2xl card.
 */
import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

const SIZE_CLASSES = {
  md: 'sm:max-w-2xl',
  lg: 'sm:max-w-3xl',
}

export default function NutritionDialog({
  open,
  onClose,
  title,
  footer,
  size = 'md',
  children,
}) {
  const { t } = useTranslation()
  const sizeClass = SIZE_CLASSES[size] ?? SIZE_CLASSES.md

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            aria-hidden="true"
          />
        </Transition.Child>

        <div className="fixed inset-0 flex items-end justify-center sm:items-center sm:p-4">
          <Transition.Child
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="translate-y-full sm:translate-y-0 sm:opacity-0 sm:scale-95"
            enterTo="translate-y-0 sm:opacity-100 sm:scale-100"
            leave="transition ease-in duration-150"
            leaveFrom="translate-y-0 sm:opacity-100 sm:scale-100"
            leaveTo="translate-y-full sm:translate-y-0 sm:opacity-0 sm:scale-95"
          >
            <Dialog.Panel
              className={clsx(
                'w-full bg-surface-container shadow-xl flex flex-col',
                'max-h-[90vh] sm:max-h-[85vh]',
                'rounded-t-2xl sm:rounded-xl',
                'pb-[env(safe-area-inset-bottom)] sm:pb-0',
                sizeClass
              )}
            >
              <div className="sticky top-0 z-10 bg-surface-container border-b border-gray-100 px-4 py-3 flex items-center justify-between rounded-t-2xl sm:rounded-t-xl">
                <Dialog.Title className="text-base font-semibold text-gray-900 truncate pr-2">
                  {title}
                </Dialog.Title>
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2 -mr-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label={t('common.close')}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-3">{children}</div>

              {footer && (
                <div className="sticky bottom-0 bg-surface-container border-t border-gray-100 px-4 py-3">
                  {footer}
                </div>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}
